/**
 * Runtime DOM translator (v2 — orphan-safe).
 *
 * Strategy:
 *   1. PASS A — walk DOM, collect every visible English text/attr value.
 *   2. Resolve cached translations from localStorage instantly.
 *   3. Fire API request for the UNKNOWN strings (Gemini batch).
 *   4. PASS B — RE-WALK the DOM (fresh references) and apply every
 *      translation found in the map. Mutate `.nodeValue` only when the
 *      current node still has the original English text. Mark the
 *      successfully-mutated node so future passes skip it; do NOT mark
 *      nodes we couldn't translate (so they remain retry-eligible).
 *
 * This fixes React's reconciler detaching text nodes during the 5s
 * await: by re-walking after the await, we always have live DOM refs.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

const STORAGE_PREFIX = "tl_i18n:";
const TRANSLATED_FLAG = "__tlI18nTranslated";
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT"]);
const SKIP_RE = /^[\s\d.,:%$€£+\-/]+$/;
const HAS_LETTER = /[A-Za-z]/;

function hashKey(lang, text) {
  let h = 0x811c9dc5;
  const s = `${lang}|${text}`;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return `${STORAGE_PREFIX}${lang}:${h.toString(16)}`;
}
function lsGet(lang, text) {
  try { return localStorage.getItem(hashKey(lang, text)); } catch { return null; }
}
function lsSet(lang, text, translation) {
  try { localStorage.setItem(hashKey(lang, text), translation); } catch (e) { /* quota */ }
}

function nodeAcceptable(node, lang) {
  if (!node.nodeValue) return false;
  if (node[TRANSLATED_FLAG] === lang) return false;
  const v = node.nodeValue.trim();
  if (v.length < 2) return false;
  if (SKIP_RE.test(v) || !HAS_LETTER.test(v)) return false;
  const parent = node.parentElement;
  if (!parent) return false;
  if (SKIP_TAGS.has(parent.tagName)) return false;
  if (parent.closest("[data-i18n-skip]")) return false;
  return true;
}

function walkTextNodes(lang) {
  const out = [];
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(n) { return nodeAcceptable(n, lang) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT; },
  });
  let n; while ((n = walker.nextNode())) out.push(n);
  return out;
}

function walkAttrTargets(lang) {
  const out = [];
  const els = document.body.querySelectorAll("[placeholder], [title], [aria-label]");
  els.forEach((el) => {
    if (SKIP_TAGS.has(el.tagName)) return;
    if (el.closest("[data-i18n-skip]")) return;
    for (const a of ["placeholder", "title", "aria-label"]) {
      const v = el.getAttribute(a);
      if (!v) continue;
      const tr = v.trim();
      if (tr.length < 2 || SKIP_RE.test(tr) || !HAS_LETTER.test(tr)) continue;
      const flagKey = `${TRANSLATED_FLAG}_${a}`;
      if (el[flagKey] === lang) continue;
      out.push({ el, attr: a, value: v, flagKey });
    }
  });
  return out;
}

async function fetchTranslations(unknowns, lang) {
  if (unknowns.length === 0) return {};
  const uniq = Array.from(new Set(unknowns));
  // Parallel batches (5 concurrent) cut cold-cache wall time roughly in half
  const slices = [];
  for (let i = 0; i < uniq.length; i += 50) slices.push(uniq.slice(i, i + 50));
  const results = await Promise.all(slices.map(async (slice) => {
    try {
      const r = await api.post("/i18n/translate", { texts: slice, target: lang });
      const arr = r.data?.translations || [];
      const local = {};
      slice.forEach((src, idx) => {
        const dst = arr[idx];
        if (dst && typeof dst === "string") {
          local[src] = dst;
          lsSet(lang, src, dst);
        }
      });
      return local;
    } catch (e) {
      return {};
    }
  }));
  return Object.assign({}, ...results);
}

function applyTranslations(lang, map) {
  // PASS B — fresh DOM walk
  let mutated = 0;
  const nodes = walkTextNodes(lang);
  nodes.forEach((n) => {
    const raw = n.nodeValue;
    const v = raw.trim();
    const tr = map[v];
    if (!tr || tr === v) return; // do NOT flag — allow future passes to retry
    const lead = raw.match(/^\s*/)[0];
    const trail = raw.match(/\s*$/)[0];
    n.nodeValue = lead + tr + trail;
    n[TRANSLATED_FLAG] = lang;
    mutated++;
  });
  const attrs = walkAttrTargets(lang);
  attrs.forEach(({ el, attr, value, flagKey }) => {
    const v = value.trim();
    const tr = map[v];
    if (!tr || tr === v) return;
    el.setAttribute(attr, tr);
    el[flagKey] = lang;
    mutated++;
  });
  return mutated;
}

export function useDomTranslator(lang) {
  const location = useLocation();
  const runningRef = useRef(false);
  const pendingRef = useRef(false);
  const [isTranslating, setIsTranslating] = useState(false);

  useEffect(() => {
    if (!lang || lang === "en") return undefined;
    let cancelled = false;

    const run = async () => {
      if (runningRef.current) {
        pendingRef.current = true;
        return;
      }
      runningRef.current = true;
      pendingRef.current = false;
      setIsTranslating(true);
      try {
        // PASS A — collect all visible English strings (current snapshot of the DOM)
        const textNodes = walkTextNodes(lang);
        const attrTargets = walkAttrTargets(lang);
        const allStrings = new Set();
        textNodes.forEach((n) => allStrings.add(n.nodeValue.trim()));
        attrTargets.forEach((a) => allStrings.add(a.value.trim()));

        // Hydrate cache from localStorage
        const map = {};
        const unknowns = [];
        for (const s of allStrings) {
          const cached = lsGet(lang, s);
          if (cached !== null) map[s] = cached;
          else unknowns.push(s);
        }

        // Apply what we have from cache IMMEDIATELY (no waiting)
        if (Object.keys(map).length > 0) applyTranslations(lang, map);

        // Fetch the unknowns
        if (unknowns.length > 0) {
          const fromApi = await fetchTranslations(unknowns, lang);
          if (cancelled) return;
          Object.assign(map, fromApi);
        }
        // PASS B — RE-walk with fresh DOM refs and apply
        applyTranslations(lang, map);
      } finally {
        runningRef.current = false;
        if (pendingRef.current && !cancelled) {
          pendingRef.current = false;
          setTimeout(() => { if (!cancelled) run(); }, 100);
        } else if (!cancelled) {
          setIsTranslating(false);
        }
      }
    };

    // Initial run + staggered FULL retries (each is a new collect+translate cycle)
    run();
    const t1 = setTimeout(() => { if (!cancelled) run(); }, 700);
    const t2 = setTimeout(() => { if (!cancelled) run(); }, 2000);
    const t3 = setTimeout(() => { if (!cancelled) run(); }, 4500);

    // Observe DOM for new nodes — instead of dropping mutations when a run is in
    // flight, signal pending and let the running run schedule a follow-up.
    let debounce = null;
    const observer = new MutationObserver(() => {
      if (runningRef.current) {
        pendingRef.current = true;
        return;
      }
      clearTimeout(debounce);
      debounce = setTimeout(run, 300);
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => {
      cancelled = true;
      observer.disconnect();
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      clearTimeout(debounce);
    };
  }, [lang, location.pathname]);

  return { isTranslating };
}
